import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Briefcase, Workflow, CalendarRange, BrainCircuit, MonitorSmartphone, Boxes, Headphones, Code2 } from "lucide-react";
import SectionLabel from "../components/SectionLabel";

const SERVICES = [
  {
    icon: Briefcase, title: "Business Process Outsourcing (BPO)",
    desc: "High-performance voice and non-voice operations powered by automated governance.",
    deliverables: ["Multichannel customer support", "Precision claims processing", "Back-office workflow automation", "Real-time performance auditing"],
    outcomes: ["30% reduction in average handling time (AHT)", "99.9% operational accuracy benchmarks", "Seamless seasonal scaling of operations"],
  },
  {
    icon: Workflow, title: "Automation & Workflow Optimization",
    desc: "Driving operational excellence through robotic process automation and digital logic.",
    deliverables: ["RPA architecting & deployment", "Enterprise workflow mapping", "Integration layer orchestration", "Real-time efficiency dashboards"],
    outcomes: ["40% reduction in manual overhead costs", "Zero-error process reliability", "Total operational transparency"],
  },
  {
    icon: CalendarRange, title: "Event Digital Operations",
    desc: "Real-time technical command and attendee orchestration for global-scale events.",
    deliverables: ["Live stream tech governance", "Virtual attendee concierge support", "Infrastructure monitoring & command", "Data capture & lead processing"],
    outcomes: ["Seamless, high-fidelity attendee UX", "Instant resolution of live tech issues", "Structured post-event insights data"],
  },
  {
    icon: BrainCircuit, title: "Knowledge Process Outsourcing (KPO)",
    desc: "Critical analytical support and technical research for high-complexity decisions.",
    deliverables: ["Deep-dive market & cohort analysis", "Custom financial modeling", "Complex technical research reports", "Corporate data visualization"],
    outcomes: ["Rapid, data-driven executive decision-making", "Significant reduction in research cycle time", "Superior market positioning accuracy"],
  },
  {
    icon: MonitorSmartphone, title: "Website Design & Management",
    desc: "Continuous platform governance and UX performance for mission-critical digital assets.",
    deliverables: ["UI/UX performance optimization", "Platform security & patch management", "Continuous SEO monitoring", "Enterprise content governance"],
    outcomes: ["Increased lead conversion efficiency", "Guaranteed platform uptime and security", "Consistent global brand integrity"],
  },
  {
    icon: Boxes, title: "Digital Products",
    desc: "Continuous software product lifecycle management to maintain velocity and scale.",
    deliverables: ["Rapid MVP prototyping", "Platform feature scaling", "User behavior analytics tracking", "Level-3 product support desk"],
    outcomes: ["Accelerated time-to-market for features", "User-centric iterative product growth", "Reduced churn via proactive support"],
  },
  {
    icon: Headphones, title: "Customer Support (Voice & Non-Voice)",
    desc: "Enterprise-grade help desk and omnichannel platform engagement across global timezones.",
    deliverables: ["24/7 technical desk management", "Multilingual omnichannel resolution", "Ticketing system orchestration", "Automated follow-up protocols"],
    outcomes: ["20% uplift in customer satisfaction (CSAT)", "Reduced cost-to-serve per engagement", "Optimized First Call Resolution (FCR)"],
  },
  {
    icon: Code2, title: "Application Development",
    desc: "Engineering secure, scalable software solutions for complex business logic and performance.",
    deliverables: ["Full-stack engineering & logic", "Legacy system modernization", "API & microservices orchestration", "Enterprise cloud migration"],
    outcomes: ["Future-proof technology architecture", "Enhanced internal team productivity", "Significantly reduced technical debt"],
  },
];

const TESTIMONIALS = [
  { q: "Operexa Gennex transformed our service delivery framework. By automating our workflow, they reduced handling time by 35% while maintaining 100% SLA adherence.", n: "MARCUS THOMSEN", t: "COO, AlphaLink Systems" },
  { q: "The depth of technical KPO expertise is unparalleled. We achieved a 20% higher CSAT score within the first quarter of deployment through their strategic customer support.", n: "SARAH JENKINS", t: "Director, Tech Innovations Ltd" },
  { q: "The tech-enabled approach reduced our cost-to-serve by 15%. Operexa Gennex is the reliable, tier-one operations partner we needed for global scale.", n: "HIROSHI TANAKA", t: "VP Operations, Global Vision" },
];

export default function Services() {
  return (
    <div data-testid="services-page" className="bg-[#050505] text-white">
      <section className="pt-32 pb-16 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <SectionLabel>Operational Excellence</SectionLabel>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter mt-6 leading-[1.05]">
              Engineered for <span className="text-[#3B82F6]">Performance.</span><br />
              Governed for Scale.
            </h1>
            <p className="mt-6 text-zinc-400 max-w-2xl">
              Operexa Gennex delivers technology-enabled operations that prioritize reliability and enterprise-grade governance.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="services-cta-talk" className="inline-flex items-center gap-2 px-5 py-3 bg-[#3B82F6] hover:bg-[#60A5FA] font-semibold">
              Talk to the Team <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/industries" data-testid="services-cta-strategy" className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 hover:border-white font-semibold">
              Our Strategy
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel>Our Specialized Services</SectionLabel>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/10">
            {SERVICES.map((s, i) => {
              const Ic = s.icon;
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: (i % 2) * 0.08 }}
                  data-testid={`detail-service-${i}`}
                  className="bg-[#050505] p-8 lg:p-10 hover:bg-[#0a0a0c] transition-colors"
                >
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 border border-white/15 flex items-center justify-center bg-[#3B82F6]/5">
                      <Ic className="w-6 h-6 text-[#3B82F6]" />
                    </div>
                    <h2 className="font-display text-xl lg:text-2xl font-semibold">{s.title}</h2>
                  </div>
                  <p className="text-zinc-400 mb-6">{s.desc}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <div className="font-mono text-[10px] tracking-[0.2em] text-[#3B82F6] uppercase mb-3">Core Deliverables</div>
                      <ul className="space-y-1.5 text-sm text-zinc-300">
                        {s.deliverables.map((d) => <li key={d} className="flex gap-2"><span className="w-1 h-1 mt-2 bg-zinc-500 block" />{d}</li>)}
                      </ul>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] tracking-[0.2em] text-[#3B82F6] uppercase mb-3">Business Outcomes</div>
                      <ul className="space-y-1.5 text-sm text-zinc-300">
                        {s.outcomes.map((o) => <li key={o} className="flex gap-2"><span className="w-1 h-1 mt-2 bg-zinc-500 block" />{o}</li>)}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/10 bg-[#08080a]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel>Voices of Trust</SectionLabel>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="border border-white/10 bg-[#0a0a0c] p-8">
                <div className="font-display text-5xl text-[#3B82F6] leading-none mb-4">"</div>
                <p className="text-zinc-300 leading-relaxed text-sm">{t.q}</p>
                <div className="mt-6 pt-4 border-t border-white/10">
                  <div className="font-display text-sm font-semibold text-white">{t.n}</div>
                  <div className="text-xs text-zinc-500">{t.t}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <SectionLabel className="justify-center">Next Steps</SectionLabel>
          <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight mt-5 max-w-3xl mx-auto">
            Architect Your Operations Roadmap.
          </h2>
          <p className="mt-4 text-zinc-400 max-w-2xl mx-auto">
            Connect with the Operexa Gennex team to transform your enterprise operations with precision.
          </p>
          <Link to="/contact" data-testid="services-bottom-cta" className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-[#3B82F6] hover:bg-[#60A5FA] font-semibold">
            Talk to the Operexa Gennex Team <ArrowUpRight className="w-4 h-4" />
          </Link>
          <div className="mt-3 font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">Response time: &lt; 4 hours</div>
        </div>
      </section>
    </div>
  );
}
